import ColorPicker from 'monday-ui-react-core/dist/ColorPicker';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  background-color: ${({ disabled, error }) =>
    disabled ? '#ECEDF5' : 'white'};
  border: ${({ disabled, error }) =>
    disabled
      ? '1px solid transparent'
      : error
      ? '1px solid red !important'
      : '1px solid #c3c6d4'};
  border-radius: 5px;
  width: 100%;
  text-align: left;
  padding-left: 16px;
  height: 32px;
  color: ${({ disabled }) => (disabled ? 'rgba(50, 51, 56, 0.38)' : '#676879')};
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
  font-family: Inter, Avenir, Helvetica, Arial, sans-serif;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    border-color: ${({ disabled }) => (disabled ? 'transparent' : 'black')};
  }
  &:focus {
    border-color: ${({ disabled }) => (disabled ? 'transparent' : '#0073ea')};
  }
  .dd-error {
    border-color: red !important;
  }
`;

const DropdownContent = styled.div`
  margin-top: 8px;
  position: absolute;
  background-color: white;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  width: 100%;
  border-radius: 5px;

  .w-100 {
    width: 100% !important;
  }
`;

const IconContainer = styled.div`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : '')};
  width: 24px;
  height: 24px;
  margin-right: 3px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? 'transparent' : '#dcdfec'};
  }
`;

const StyledSVG = styled.svg`
  outline: none;
  pointer-events: none;
`;

const StyledColorPicker = styled(ColorPicker)`
  width: 100%;

  &.dialog-content-container {
    width: 100% !important;
    padding: 5px !important;
  }

  .color-picker-content--wrapper {
    width: 100% !important;
  }

  .color-picker-content--wrapper .color-picker {
    justify-content: start;
  }
`;

const CustomColorPicker = ({ value, handleInput, disabled, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const triggerDropdown = () => {
    if (!disabled) {
      setIsOpen((s) => !s);
    }
  };

  const DropdownIcon = () => {
    return (
      <StyledSVG
        viewBox="0 0 20 20"
        fill="currentColor"
        width="16px"
        height="16px"
        role="button"
        tabindex="-1"
        aria-hidden="false"
      >
        <path
          d="M10.5303 12.5303L10 12L9.46967 12.5303C9.76256 12.8232 10.2374 12.8232 10.5303 12.5303ZM10 10.9393L6.53033 7.46967C6.23744 7.17678 5.76256 7.17678 5.46967 7.46967C5.17678 7.76256 5.17678 8.23744 5.46967 8.53033L9.46967 12.5303L10 12L10.5303 12.5303L14.5303 8.53033C14.8232 8.23744 14.8232 7.76256 14.5303 7.46967C14.2374 7.17678 13.7626 7.17678 13.4697 7.46967L10 10.9393Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        ></path>
      </StyledSVG>
    );
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleColorSave = (color) => {
    // monday-ui ColorPicker.onSave returns an array (e.g. ["#396AFF"]) even in single-select mode.
    // Unwrap to a plain string so the form value matches the yup schema (yup.string()).
    const normalized = Array.isArray(color) ? color[0] ?? '' : color;
    if (String(normalized) !== String(value ?? '')) {
      handleInput(normalized);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Define your custom colors here
  const customColors = [
    '#040930',
    '#396AFF',
    '#0DB691',
    '#FF8282',
    '#647E9E',
    '#FF2929',
    '#A09C9C',
    '#FFC107',
    '#93A689',
    '#F5F6F8',
  ];

  return (
    <>
      <DropdownContainer ref={dropdownRef}>
        <DropdownButton
          onClick={triggerDropdown}
          disabled={disabled}
          error={!!error}
        >
          {value || 'Pick Color'}
          <IconContainer isOpen={isOpen} disabled={disabled}>
            <DropdownIcon />
          </IconContainer>
        </DropdownButton>
        {isOpen ? (
          <DropdownContent>
            <StyledColorPicker
              numberOfColorsInLine="6"
              colorsList={customColors}
              forceUseRawColorList
              isBlackListMode
              value={value || '#F5F6F8'}
              onSave={handleColorSave}
            />
          </DropdownContent>
        ) : null}
      </DropdownContainer>
    </>
  );
};

export default CustomColorPicker;
